<template>
  <div>
    <div class="flex justify-between gap-4 mb-2">
      <div class="flex flex-col">
        <span class="text-xl text-primary">
          {{ $t('message.table.header.movements') }}
        </span>
        <p class="mt-2 text-sm md:mb-4 text-secondary">
          {{ $t('message.table.header.lastAccountUpdate') }}
          {{ lastUpdateTime | relativeTime }}
        </p>
      </div>
      <div class="flex flex-col gap-4">
        <button
          v-if="isRefreshIntentEnabled"
          class="flex h-10 px-2 text-white rounded-md button bg-sc-blue shadow-sc-button hover:bg-sc-blue-dark disabled:text-gray-400 disabled:bg-sc-blue"
          @click="sendRefreshIntentRequest"
          :disabled="isRefreshIntentBlocked"
        >
          {{
            isRefreshIntentBlocked ?
              $t('message.table.header.refreshIntentLoading') :
              $t('message.table.header.sendRefreshIntent')
          }}
        </button>
        <div class="flex flex-col flex-wrap gap-4 md:flex-row md:space-x-4">
          <movements-download-button
            v-if="showExportButton"
          />
          <button
            @click="toggleFilters"
            v-if="mobile"
          >
            <div class="flex content-start px-3 py-1 bg-white border-none rounded-md md:px-2 button shadow-sc-button">
              <img
                :src="require('@assets/images/filter.svg')"
                alt="filter icon"
                class="h-6"
              >
              <div
                class="ml-2 focus:outline-none"
              >
                <span class="text-sm text-primary">Filtrar</span>
              </div>
            </div>
          </button>
          <div
            class="hidden h-8 space-x-2 border rounded shadow md:flex"
            v-if="showFilters"
          >
            <button
              @click="changeDescriptionFilter"
            >
              <img
                :src="require('@assets/images/lens.svg')"
                alt="lupa"
                class="w-6 p-1 cursor-pointer"
              >
            </button>
            <input
              type="text"
              name="description-filter"
              id="description-filter"
              v-model="descriptionFilter"
              :placeholder="$t('message.table.header.textFilter')"
              class="text-xs w-72 focus:outline-none"
              :disabled="userDefaultFilters.description || loading"
              @keyup.enter="changeDescriptionFilter"
            >
          </div>
        </div>
      </div>
    </div>
    <div
      class="flex h-8 mt-8 space-x-2 border rounded shadow md:hidden"
      v-if="showFilters"
    >
      <button
        @click="changeDescriptionFilter"
      >
        <img
          :src="require('@assets/images/lens.svg')"
          alt="lupa"
          class="w-6 p-1 cursor-pointer"
        >
      </button>
      <input
        type="text"
        name="description-filter"
        id="description-filter"
        v-model="descriptionFilter"
        :placeholder="$t('message.table.header.textFilter')"
        class="text-xs w-72 focus:outline-none"
        :disabled="userDefaultFilters.description || loading"
        @keyup.enter="changeDescriptionFilter"
      >
    </div>
    <filters-selector
      v-if="showFilters"
      :accounts-filter="accountsFilters"
      :post-date-filter="postDateFilter"
      :institution-filter="institutionFilter"
      :from-amount-filter="fromAmountFilter"
      :to-amount-filter="toAmountFilter"
      :amount-filter="amountFilter"
      :inbound-filter="inboundFilter"
      :outbound-filter="outboundFilter"
      :status-filter="statusFilter"
      :type-filter="typeFilter"
      :user-default-filters="userDefaultFilters"
      :loading="loading"
      @filterChanged="changeFilterValue"
      @clearCurrentPostDateFilter="clearCurrentPostDateFilter"
      @clearCurrentAmountFilter="clearCurrentAmountFilter"
      @clearCurrentAmountRangeFilter="clearCurrentAmountRangeFilter"
      @selectAllAccounts="selectAllAccounts"
      @changeAccountFilter="changeAccountFilter"
    >
      <template v-slot:clearFiltersButton>
        <div
          class="flex justify-end w-full md:w-auto"
        >
          <button
            @click="clearFilters"
            class="text-center border-none rounded-md button shadow-sc-button bg-sc-gray-lighter hover:text-gray-700"
          >
            <p class="text-sm text-primary">
              {{ $t('message.table.header.clearFilters') }}
            </p>
          </button>
        </div>
      </template>
      <template v-slot:customFilters>
        <div
          v-if="extraColumnsNames.length > 0"
          class="flex flex-col space-y-4 md:flex-row md:space-x-2 md:space-y-0"
        >
          <div
            v-for="columnName in enabledColumnNames"
            :key="columnName"
          >
            <b-dropdown
              v-if="extraColumnsSchema[columnName].type === 'Boolean'"
              @change="(value) => { booleanUpdateExtraColumnsFilter(columnName, value); }"
            >
              <button
                class="flex items-center px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
                slot="trigger"
              >
                <p class="text-secondary">
                  {{ columnName }}
                </p>
                <span>{{ toggleTypes[getValueExtraColumnsFilterBoolean(columnName)] }}</span>
                <img
                  :src="require('@assets/images/chevron-down-outline.svg')"
                  alt="flecha para abajo"
                >
              </button>
              <b-dropdown-item
                class="text-xs text-primary"
                type="is-primary"
                v-for="option in Object.keys(toggleTypes)"
                :key="option"
                :value="option"
              >
                {{ toggleTypes[option] }}
              </b-dropdown-item>
            </b-dropdown>
            <b-dropdown
              v-else-if="extraColumnsSchema[columnName].type === 'Enum'"
            >
              <button
                class="flex items-center px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
                slot="trigger"
              >
                <p class="text-secondary">
                  {{ columnName }}
                </p>
                <span>
                  {{ enumTypes.all }}
                </span>
                <img
                  :src="require('@assets/images/chevron-down-outline.svg')"
                  alt="flecha para abajo"
                >
              </button>
              <b-checkbox
                class="w-full pb-1 pl-2 text-xs border-b text-primary"
                :value="getAllExtraColumnsSelectedEnum(columnName)"
                @input="enumUpdateAllExtraColumnsFilter(columnName, $event)"
              >
                <p>
                  {{ getAllExtraColumnsSelectedEnum(columnName) ?
                    $t('message.accounts.unselectAll') : $t('message.accounts.selectAll') }}
                </p>
              </b-checkbox>
              <div
                v-for="enumName in extraColumnsSchema[columnName].options.tags"
                :key="enumName"
              >
                <b-checkbox
                  :value="getValueExtraColumnsFilterEnum(columnName, enumName)"
                  @input="enumUpdateExtraColumnsFilter(columnName, enumName, $event)"
                  class="w-full pb-1 pl-2 text-xs text-primary"
                  type="is-primary"
                >
                  <p>
                    {{ enumName }}
                  </p>
                </b-checkbox>
              </div>
            </b-dropdown>
            <div
              class="flex w-48 mt-4 ml-2 rounded"
              v-if="extraColumnsSchema[columnName].type === 'Date'"
            >
              <b-datepicker
                :value="getValueExtraColumnsFilterDate(columnName)"
                @input="dateUpdateExtraColumnsFilter(columnName, $event)"
                icon="calendar-today"
                size="is-small"
                locale="ES-CL"
                :placeholder="columnName"
                range
                data-hj-allow
              />
              <div
                class="px-4 font-bold text-white align-baseline border-blue-700 border-none rounded bg-sc-blue is-small button hover:bg-primary"
              >
                <button
                  @click="clearExtraColumnsCurrentPostDate(columnName)"
                >
                  <b-icon
                    icon="close-circle"
                    size="is-small"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </filters-selector>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { camelize, decamelize } from 'humps';
import { keyParam, keyParamEnum } from '../../../helpers/createExtraColumnQueryParams';
import searchDebounce from '../../utils/searchDebounce';
import isMobile from '../../utils/isMobile';
import MovementsDownloadButton from './movements-download-button.vue';
import FiltersSelector from '../filters-selector.vue';
import RefreshIntentApi from '../../api/refresh-intents.js';

export default {
  data() {
    return {
      headerAllIndex: 2,
      toggleTypes: {
        'all': this.$tc('message.table.header.all', this.headerAllIndex),
        'true': 'True',
        'false': 'False',
      },
      enumTypes: {
        'all': this.$tc('message.table.header.all', this.headerAllIndex),
      },
      descriptionFilter: undefined,
      showFilters: !isMobile(),
      mobile: isMobile(),
      refreshIntentButtonBlockedAt: undefined,
    };
  },
  components: {
    MovementsDownloadButton,
    FiltersSelector,
  },
  props: {
    showExportButton: {
      type: Boolean,
      required: true,
    },
    isRefreshIntentEnabled: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapGetters([
      'booleanGetExtraColumnsFilterValue',
      'enumGetExtraColumnsFilterValue',
      'enumAllExtraColumnsOptionsSelected',
      'textGetExtraColumnsFilterValue',
      'dateGetExtraColumnsFilterValue',
    ]),
    accountsFilters() {
      return this.$store.state.movements.accountsFilters;
    },
    enabledColumnNames() {
      return this.extraColumnsNames.filter(columnName => this.extraColumnsSchema[columnName].enableFilter);
    },
    typeFilter: {
      get() {
        return this.$store.state.movements.typeFilter;
      },
      set(filter) {
        this.changeFilter({ key: 'type', value: filter });
      },
    },
    postDateFilter: {
      get() {
        return this.$store.state.movements.postDateFilter;
      },
      set(filter) {
        this.changeFilter({ key: 'postDate', value: filter });
      },
    },
    isRefreshIntentBlocked() {
      // eslint-disable-next-line no-magic-numbers
      const FIVE_MINUTES = 5 * 60 * 1000;

      if (this.refreshIntentButtonBlockedAt) {
        return Date.now() - this.refreshIntentButtonBlockedAt < FIVE_MINUTES;
      }

      return false;
    },
    fromAmountFilter: {
      get() {
        return this.$store.state.movements.fromAmountFilter;
      },
      set(filter) {
        let parsedFilter = filter.replace(/\./g, '').replace(',', '.');
        if (filter) parsedFilter = Number(parsedFilter);
        this.debouncedFilter('fromAmount', parsedFilter);
      },
    },
    toAmountFilter: {
      get() {
        return this.$store.state.movements.toAmountFilter;
      },
      set(filter) {
        let parsedFilter = filter.replace(/\./g, '').replace(',', '.');
        if (filter) parsedFilter = Number(parsedFilter);
        this.debouncedFilter('toAmount', parsedFilter);
      },
    },
    amountFilter: {
      get() {
        return this.$store.state.movements.amountFilter;
      },
      set(filter) {
        let parsedFilter = filter.replace(/\./g, '').replace(',', '.');
        if (filter) parsedFilter = Number(parsedFilter);
        this.debouncedFilter('amount', parsedFilter);
      },

    },
    institutionFilter: {
      get() {
        return this.$store.state.movements.institutionFilter;
      },
      set(filter) {
        this.changeFilter({ key: 'institution', value: filter });
      },
    },
    inboundFilter: {
      get() {
        return this.$store.state.movements.inboundFilter;
      },
      set(filter) {
        this.changeFilter({ key: 'inbound', value: filter });
      },
    },
    outboundFilter: {
      get() {
        return this.$store.state.movements.outboundFilter;
      },
      set(filter) {
        this.changeFilter({ key: 'outbound', value: filter });
      },
    },
    statusFilter: {
      get() {
        return this.$store.state.movements.statusFilter;
      },
      set(filter) {
        this.changeFilter({ key: 'status', value: filter });
      },
    },
    activeAccounts() {
      return this.$store.getters.activeAccounts;
    },
    lastUpdateTime() {
      const lastUpdate = Object.values(this.accountsFilters).reduce((acc, curr) => (
        new Date(acc.fintocRefreshedAt) >= new Date(curr.fintocRefreshedAt) ? acc : curr)
      , { fintocRefreshedAt: undefined }).fintocRefreshedAt;

      return lastUpdate;
    },
    extraColumnsSchema() {
      return this.$store.getters.extraColumnsSchema;
    },
    extraColumnsNames() {
      return this.$store.getters.extraColumnsNames;
    },
    userDefaultFilters() {
      return this.$store.state.movements.userDefaultFilters;
    },
    loading() {
      return this.$store.state.movements.loading;
    },
    entityId() {
      return this.$store.state.entities.entityId;
    },
  },
  methods: {
    sendRefreshIntentRequest() {
      RefreshIntentApi.bankAccountRefreshIntent(
        this.entityId,
        this.activeAccounts,
      );
      this.refreshIntentButtonBlockedAt = Date.now();
      this.$buefy.toast.open({
        message: 'Se ha enviado la solicitud de actualización',
        duration: 3000,
      });
    },
    getValueExtraColumnsFilterBoolean(columnName) {
      return this.booleanGetExtraColumnsFilterValue(columnName) || 'all';
    },
    getAllExtraColumnsSelectedEnum(columnName) {
      return this.enumAllExtraColumnsOptionsSelected(columnName);
    },
    getValueExtraColumnsFilterEnum(columnName, enumName) {
      return this.enumGetExtraColumnsFilterValue(columnName, enumName);
    },
    getValueExtraColumnsFilterText(columnName) {
      return this.textGetExtraColumnsFilterValue(columnName);
    },
    getValueExtraColumnsFilterDate(columnName) {
      const dates = this.dateGetExtraColumnsFilterValue(columnName) || [];
      const allUndefined = dates.every((val) => val === undefined);

      return allUndefined ? undefined : dates;
    },
    camelize(string) {
      return camelize(string);
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    changeFilter(param) {
      this.$store.dispatch('changeFilter', param);
      this.addParamToUrl(param);
      this.addParamToUrl({ key: 'page', value: 1 });
    },
    booleanChangeExtraColumnsFilter(param) {
      this.$store.dispatch('booleanChangeExtraColumnsFilter', param);
      const newKey = keyParam(param.key, 'boolean');
      this.addParamToUrl({ key: newKey, value: param.value });
      this.addParamToUrl({ key: 'page', value: 1 });
    },
    booleanUpdateExtraColumnsFilter(columnName, filter) {
      this.booleanChangeExtraColumnsFilter({ key: columnName, value: filter });
    },
    enumChangeExtraColumnsFilter(param) {
      this.$store.dispatch('enumChangeExtraColumnsFilter', param);
      const newKey = keyParamEnum(param.key, 'enum', param.enum);
      this.addParamToUrl({ key: newKey, value: param.value });
      this.addParamToUrl({ key: 'page', value: 1 });
    },
    enumChangeAllExtraColumnsFilter(param) {
      this.$store.dispatch('enumChangeAllExtraColumnsFilter', param);
      this.addParamToUrl({ key: 'page', value: 1 });
    },
    enumUpdateExtraColumnsFilter(columnName, enumName, selected) {
      this.enumChangeExtraColumnsFilter({ key: columnName, enum: enumName, value: selected });
    },
    enumUpdateAllExtraColumnsFilter(columnName, selected) {
      this.enumChangeAllExtraColumnsFilter({ key: columnName, value: selected });
    },
    textChangeExtraColumnsFilter(param) {
      this.$store.dispatch('textChangeExtraColumnsFilter', param);
      const newKey = keyParam(param.key, 'text');
      this.addParamToUrl({ key: newKey, value: param.value });
      this.addParamToUrl({ key: 'page', value: 1 });
    },
    dateChangeExtraColumnsFilter(param) {
      this.$store.dispatch('dateChangeExtraColumnsFilter', param);
      const newKey = keyParam(param.key, 'date');
      this.addParamToUrl({ key: newKey,
        value: `${param.startDate},${param.endDate}` });
      this.addParamToUrl({ key: 'page', value: 1 });
    },
    dateUpdateExtraColumnsFilter(columnName, filter) {
      this.dateChangeExtraColumnsFilter(
        { key: columnName, startDate: filter[0], endDate: filter[1] },
      );
    },
    changeFilterValue(filterData) {
      this[filterData.filterName] = filterData.filterValue;
    },
    changeAccountFilter(accountId) {
      this.$store.dispatch('changeAccountFilter', accountId);
      this.addParamToUrl({ key: 'accounts[]', value: this.activeAccounts });
    },
    selectAllAccounts(value) {
      this.$store.dispatch('changeAllAccountsFilters', value);
      this.addParamToUrl({ key: 'accounts[]', value: this.activeAccounts });
    },
    clearExtraColumnsCurrentPostDate(columnName) {
      this.dateChangeExtraColumnsFilter({ key: columnName, value: undefined });
    },
    clearCurrentPostDateFilter() {
      this.$store.dispatch('changeFilter', { key: 'postDate', value: undefined });
      this.addParamToUrl({ key: 'postDate', value: '' });
    },
    clearCurrentAmountRangeFilter() {
      this.$store.dispatch('changeFilter', { key: 'fromAmount', value: undefined });
      this.$store.dispatch('changeFilter', { key: 'toAmount', value: undefined });
      this.addParamToUrl({ key: 'fromAmount', value: '' });
      this.addParamToUrl({ key: 'toAmount', value: '' });
    },
    clearCurrentAmountFilter() {
      this.$store.dispatch('changeFilter', { key: 'amount', value: undefined });
      this.addParamToUrl({ key: 'amount', value: '' });
    },
    clearFilters() {
      this.$store.dispatch('clearFilters');
      window.history.replaceState(
        {}, '', `${location.pathname}`,
      );
      this.descriptionFilter = undefined;
    },
    addParamToUrl(newParam) {
      const params = new URLSearchParams(window.location.search);
      params.set(decamelize(newParam.key), newParam.value);
      window.history.replaceState(
        {}, '', `${location.pathname}?${params}`,
      );
    },
    debouncedFilter: searchDebounce(function (keyFilter, valueFilter) {
      this.changeFilter({ key: keyFilter, value: valueFilter });
    }),
    changeDescriptionFilter() {
      this.extraColumnsNames.forEach((columnName) => {
        if (this.extraColumnsSchema[columnName].type === 'String') {
          this.textChangeExtraColumnsFilter({ key: columnName, value: this.descriptionFilter });
        }
      });
      this.changeFilter({ key: 'description', value: this.descriptionFilter });
    },
  },
};
</script>

<style lang="scss">
  .b-radio.radio > .control-label {
    padding-left: 4px;
  }
  .dropdown-menu {
    z-index: 1000;
  }
</style>
